import type { TFunction } from 'i18next';
import { z } from 'zod';
import type { DocTemplateOption, FleetList, FleetVessel, Vessel } from './api.js';

/** Filo özeti: özet kartları için (docs/ekranlar: "Total", "Overdue" kartları). */
export function summarizeFleet(vessels: readonly Vessel[]) {
  const active = vessels.filter((vessel) => vessel.activated).length;
  return { total: vessels.length, active, pending: vessels.length - active };
}

/** Geminin kurulum durumu (ADR 0142 §1); eski sunucu göndermezse kurulum. */
export function setupStateOf(vessel: Vessel): 'setup' | 'live' {
  return vessel.setupState === 'live' ? 'live' : 'setup';
}

/** "Kullanıma al" yalnız gemi sunucusu etkinleşmiş ve kurulumdaki gemide (ADR 0142 §2). */
export function canGoLive(vessel: Vessel): boolean {
  return vessel.activated && setupStateOf(vessel) === 'setup';
}

/**
 * Fleet düğmesinin görünümü (ADR 0151 §1): yetkiliye düğme, yetkisi olmayana düğme yerine kısa
 * açıklama (hangi yetki, varsayılanda hangi rol), geminin durumuna uymayan düğme hiç yok.
 */
export type FleetControl = 'button' | 'denied' | 'hidden';

/**
 * Açıklamadaki yetki: sunucunun o uçta denetlediği matris hücresi ve kapsamı (`apps/server/src/
 * office/vessels.ts`). Kurulum kodu ve sunucu değişimi aynı uçtur, gemiye göre sorulur; gemi
 * ekleme ve "Go live" gemisiz (filo) sorulur: gemi kapsamlı hücre yetmez (ADR 0151 §1, Ek).
 * Varsayılan matriste dördü de yalnız Company Administrator'da (`packages/db/src/seed.ts`).
 */
export const FLEET_PERMISSIONS = {
  add: { module: 'settings', action: 'create', scope: 'fleet' },
  setupCode: { module: 'devices', action: 'create', scope: 'vessel' },
  replaceServer: { module: 'devices', action: 'create', scope: 'vessel' },
  goLive: { module: 'settings', action: 'edit', scope: 'fleet' },
  // Gemi özellikleri de gemi satırını yazar (ADR 0151 Ek)
  particulars: { module: 'settings', action: 'edit', scope: 'fleet' },
} as const;

/**
 * Düğme yerine açıklama (ADR 0151 §1): matristeki yetki (Modül ▸ İşlem), kapsamı ("filo
 * genelinde", "bu gemide"; Ek) ve varsayılanda hangi rolde. `text` metnin anahtarı (menüde kısa).
 */
export function fleetDeniedText(
  t: TFunction,
  kind: keyof typeof FLEET_PERMISSIONS,
  text: string = kind,
): string {
  const { module, action, scope } = FLEET_PERMISSIONS[kind];
  return t(`fleet.denied.${text}`, {
    permission: t('fleet.denied.permission', {
      module: t(`setup.matrix.moduleNames.${module}`),
      action: t(`setup.matrix.actions.${action}`),
    }),
    scope: t(`fleet.denied.scope.${scope}`),
    role: t('setup.roles.names.company_admin'),
  });
}

/** Bayrak yoksa (eski sunucu) düğme eskisi gibi açık; sunucu yine denetler. */
function control(applies: boolean, allowed: boolean | undefined): FleetControl {
  if (!applies) return 'hidden';
  return allowed === false ? 'denied' : 'button';
}

/**
 * Gemi satırının düğmeleri: etkin olmayan gemide kurulum kodu, etkin gemide sunucu değişimi,
 * etkin ve kurulumdaki gemide "Go live" (ADR 0142 §2).
 */
export function vesselControls(vessel: FleetVessel): {
  setupCode: FleetControl;
  replaceServer: FleetControl;
  goLive: FleetControl;
} {
  return {
    setupCode: control(!vessel.activated, vessel.canSetupCode),
    replaceServer: control(vessel.activated, vessel.canReplaceServer),
    goLive: control(canGoLive(vessel), vessel.canGoLive),
  };
}

/**
 * "Add vessel" düğmesi (başlık ve boş durum). Liste yokken (yükleniyor ya da yüklenemedi) yetki
 * bilinmez: düğme gösterilmez, yoksa yetkisiz kişi basıp 403 alırdı (ADR 0151 §1).
 */
export function addVesselControl(list: Pick<FleetList, 'canCreateVessel'> | null): FleetControl {
  if (list === null) return 'hidden';
  return control(true, list.canCreateVessel);
}

/**
 * Gemi özellikleri çekmecesi salt okunur mu (ADR 0151 Ek): sunucu filo kapsamlı `settings.edit`
 * yok dediyse. Görüntüleme herkese açık; bayrak yoksa (eski sunucu) eskisi gibi düzenlenir.
 */
export function particularsReadOnly(list: Pick<FleetList, 'canEditParticulars'> | null): boolean {
  return list?.canEditParticulars === false;
}

/** IANA saat dilimi mi (sunucudaki kontrolle aynı). */
export function isTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/**
 * "Gemi ekle" formu: sunucudaki şemayla aynı kurallar (`apps/server/src/office/vessels.ts`).
 * Hata iletileri çeviri anahtarıdır.
 */
export const newVesselSchema = z.object({
  name: z.string().trim().min(1, 'fleet.addForm.nameRequired').max(200),
  imo: z.string().regex(/^\d{7}$/, 'fleet.addForm.imoInvalid'),
  timeZone: z.string().refine(isTimeZone, 'errors.VALIDATION_FAILED'),
  /** Doküman şablonu (ADR 0037 §4): yalnız şirkete açık şablonlardan; sunucu yeniden denetler. */
  docTemplateId: z.uuid('fleet.addForm.templateRequired'),
});

export type NewVesselValues = z.infer<typeof newVesselSchema>;

/** Seçilebilir saat dilimleri; tarayıcı listeyi veremezse en azından UTC ve tarayıcınınki. */
export function timeZoneOptions(browserZone: string): string[] {
  const all =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  return [...new Set(['UTC', browserZone, ...all])].filter(isTimeZone).sort();
}

/** Şablon listesinin durumu (`GET /api/office/doc-templates`). */
export type TemplateListState =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; defaultTemplateId: string | null; templates: DocTemplateOption[] };

export interface TemplateField {
  options: { value: string; label: string }[];
  /** Önceden seçili: şirketin varsayılanı, yoksa ilk şablon. */
  value: string | null;
  /** Tek şablon varsa gösterilir ama değiştirilemez; yüklenirken ya da hatada da kapalı. */
  disabled: boolean;
  /** Gösterilecek hata kodu: liste yüklenemedi ya da şirkette açık şablon yok. */
  error: string | null;
  /** Şablonsuz gemi açılmaz (ADR 0038 §5: `DOC_TEMPLATE_REQUIRED`). */
  canSubmit: boolean;
}

/**
 * "Document template" alanı (ADR 0037 §4 eki; ADR 0038 §5): listede yalnız şirkete açık
 * şablonlar; varsayılan önceden seçili; tek şablon varsa değiştirilemez. Liste yüklenemezse ya da
 * boşsa form gönderilemez.
 */
export function templateField(state: TemplateListState): TemplateField {
  if (state.kind !== 'ready') {
    return {
      options: [],
      value: null,
      disabled: true,
      error: state.kind === 'error' ? state.code : null,
      canSubmit: false,
    };
  }
  const options = state.templates.map((t) => ({ value: t.id, label: `${t.name} (v${t.version})` }));
  if (options.length === 0) {
    return {
      options,
      value: null,
      disabled: true,
      error: 'DOC_TEMPLATE_REQUIRED',
      canSubmit: false,
    };
  }
  const preferred =
    state.templates.find((t) => t.id === state.defaultTemplateId) ??
    state.templates.find((t) => t.isDefault) ??
    state.templates[0];
  return {
    options,
    value: preferred?.id ?? null,
    disabled: options.length === 1,
    error: null,
    canSubmit: true,
  };
}
